// The workspace mode's main column: the saved boards side by side, with
// evidence panes shown when a board asks for them. Session previews sit over
// the panes until they are closed or mounted into the active pane.
import { useCallback, useEffect, useRef, useState } from "react";
import FleetCanvas from "../components/FleetCanvas.jsx";
import TraceCanvasOverlay from "../components/TraceCanvasOverlay.jsx";
import WorkspaceDeck, { blankDeckPane } from "../components/WorkspaceDeck.jsx";
import WorkspaceDock from "../components/WorkspaceDock.jsx";
import WorkspaceInventory from "../components/WorkspaceInventory.jsx";
import { SESSION_DRAG_TYPE } from "../components/WorkspaceThreads.jsx";
import { requestWorkspaceFocus } from "../lib/workspaceStore.js";
import { onDeckWorkspace, setDeckBlankInFront, showWorkspaceInDeck } from "../lib/deckRequests.js";
import { closeTracePreview, layOnTraceCanvas, openTraceCanvas, traceCanvasOpen, useTraceCanvasOpen } from "../lib/traceCanvasStore.js";

// Read where it renders: a load comes through the sign-in callback first.
const fleetMode = () => new URLSearchParams(globalThis.location?.search || "").get("fleet") === "mode";
const draggingSession = (e) => [...(e.dataTransfer?.types || [])].includes(SESSION_DRAG_TYPE);
// Inside its frames the workspace stands on its own ground, a step off the
// page's: the board and its evidence panes draw on it. The gutters
// around and between the frames keep the page's, so each frame is the one
// edge between the two.
const WORKSPACE_GROUND = { "--bg": "var(--workspace-bg)", "--plot-bg": "var(--workspace-bg)" };

// With no evidence selected, the boards use the whole page.
function EmptyBoard({ range, onRangeChange = null }) {
  const open = useTraceCanvasOpen();
  return (
    <WorkspaceDock chatEnabled={!!open} scope="home" chatFor={!open ? null : open.inDeck ? open.id : undefined} behind={open?.preview ? open.id : null} opened={open?.id ?? null} data-workspace-home-empty="">
      <WorkspaceDeck range={range} onRangeChange={onRangeChange} />
    </WorkspaceDock>
  );
}

export default function WorkspaceHome({ range = "1M", onRangeChange = null, onFind = null }) {
  // Coming back to the home comes back to its panes as they were left (the
  // deck keeps them), not to the last workspace's board over the page.
  const open = useTraceCanvasOpen();
  const [dropping, setDropping] = useState(false);
  // Every workspace opens in a pane: one opened from anywhere else (a chat's
  // investigation, a case) goes to the deck, with what its opener holds for
  // its board. A session previewed stays over the panes.
  useEffect(() => {
    if (!open || open.inDeck || open.preview || fleetMode()) return;
    // A preview mounted went into its pane as it was mounted.
    const now = traceCanvasOpen();
    if (now?.inDeck && now.id === open.id) return;
    showWorkspaceInDeck(open.id, { trace: open.trace || null, focus: open.focus || null });
  }, [open]);
  // A preview left up is put away with the home, not found over it again.
  useEffect(() => () => closeTracePreview(), []);

  // The page opens on the team's investigations unless a board is open. A
  // board opened (from a row, the sidebar, Findings) brings the deck up, and
  // the deck stays, blank panes and previews included, until All
  // investigations brings the inventory back.
  const inventory = !fleetMode();
  const [listing, setListing] = useState(() => !open || !!open.preview);
  useEffect(() => {
    if (open && !open.preview) setListing(false);
  }, [open]);
  // While the inventory is up the deck is not: a board asked of it, or a
  // fresh pane to start one in (New investigation, or the rail's
  // Investigations picked again), brings it up and goes there.
  const asked = useRef(null);
  useEffect(() => {
    if (!inventory || !listing) return undefined;
    setDeckBlankInFront(true);
    const off = onDeckWorkspace((id, extra) => {
      if (!id && !extra?.fresh) return;
      if (id) asked.current = [id, extra];
      else blankDeckPane();
      setListing(false);
    });
    return () => {
      off();
      setDeckBlankInFront(false);
    };
  }, [inventory, listing]);
  useEffect(() => {
    if (listing || !asked.current) return;
    const [id, extra] = asked.current;
    asked.current = null;
    showWorkspaceInDeck(id, extra);
  }, [listing]);

  // An open workspace has the page to itself: the drawer of workspaces and
  // sessions folds away once one is open and stays folded while the deck is
  // up (a new investigation started in a pane included), so the page never
  // resizes under the reader; it comes back as it was with the list of
  // investigations, or another mode. A session previewed over the workspace
  // leaves things as they are.
  const [docked, setDocked] = useState(false);
  useEffect(() => {
    if (open && !open.preview) setDocked(true);
  }, [open]);
  useEffect(() => {
    if (listing) setDocked(false);
  }, [listing]);
  useEffect(() => {
    if (!docked) return undefined;
    window.dispatchEvent(new CustomEvent("origin-investigation-workspace", { detail: true }));
    return () => window.dispatchEvent(new CustomEvent("origin-investigation-workspace", { detail: false }));
  }, [docked]);

  // A session dropped on the board goes beside what it holds (or starts a
  // workspace on it, with none open).
  // The deck's panes take what is dropped on them; the fleet's old panes
  // leave the rest to the page.
  const drops = fleetMode();
  const lay = useCallback((entry) => {
    const cur = traceCanvasOpen();
    if (!cur) {
      openTraceCanvas({ id: entry.id, title: entry.title });
      return;
    }
    layOnTraceCanvas(cur.id, entry);
    requestWorkspaceFocus(entry.id);
  }, []);

  return (
    <div
      className="relative h-full min-h-0 min-w-0 bg-[color:var(--bg)] pb-[10px] pr-[10px] pt-[11px]"
      onDragOver={(e) => {
        if (!drops || !draggingSession(e)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
        if (!dropping) setDropping(true);
      }}
      onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDropping(false); }}
      onDrop={(e) => {
        if (!drops || !draggingSession(e)) return;
        e.preventDefault();
        setDropping(false);
        // A workspace's board in a pane laid it on itself.
        if (e.nativeEvent?.laidOn) return;
        try {
          const entry = JSON.parse(e.dataTransfer.getData(SESSION_DRAG_TYPE) || "null");
          if (entry?.id) lay(entry);
        } catch { /* not a session */ }
      }}
      data-workspace-home=""
      data-workspace-paper=""
    >
      {inventory && listing ? <WorkspaceInventory /> : inventory ? (
        <div className="flex h-full min-h-0 min-w-0 flex-col">
          <button type="button" onClick={() => setListing(true)} className="mb-[8px] flex h-[22px] shrink-0 items-center gap-[6px] self-start pl-[2px] text-[12.5px] text-[color:var(--muted)] hover:text-[color:var(--ink)]" data-workspace-inventory-back="">
            <svg viewBox="0 0 10 10" aria-hidden="true" className="h-[10px] w-[10px]" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"><path d="M6.5 2l-3 3 3 3" /></svg>
            All investigations
          </button>
          <div className="min-h-0 min-w-0 flex-1" style={WORKSPACE_GROUND} data-workspace-ground="">
            <EmptyBoard range={range} onRangeChange={onRangeChange} />
          </div>
        </div>
      ) : (
        <div className="h-full min-h-0 min-w-0" style={WORKSPACE_GROUND} data-workspace-ground="">
          {fleetMode() ? <FleetCanvas range={range} onRangeChange={onRangeChange} onFind={onFind} /> : <EmptyBoard range={range} onRangeChange={onRangeChange} />}
        </div>
      )}
      {dropping ? (
        <div className="pointer-events-none absolute bottom-[10px] left-0 right-[10px] top-[11px] z-[30] grid place-items-center rounded-[10px] border border-dashed border-[color:var(--line-strong)] bg-[color-mix(in_srgb,var(--ink)_8%,transparent)]" data-workspace-drop="">
          <span className="rounded-[7px] border border-[color:var(--line-strong)] bg-[color:var(--panel)] px-[10px] py-[5px] text-[12px] text-[color:var(--ink)] shadow-[var(--node-shadow-lift)]">
            {open ? "Drop to lay it on this investigation" : "Drop to start an investigation on it"}
          </span>
        </div>
      ) : null}
      {fleetMode() ? null : <TraceCanvasOverlay preview range={range} onRangeChange={onRangeChange} />}
    </div>
  );
}
